import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { observerApi } from "../services/observerApi";
import { ObserverSearchResult } from "../types/observer";

const practices = [
  { slug: "manifestation-coaches", title: "Manifestation", description: "Law of attraction, law of assumption, conscious creation, self-concept, and reality-creation teachings." },
  { slug: "tarot-readers", title: "Tarot & oracle", description: "Tarot readers, oracle readers, divination practices, public readings, courses, and communities." },
  { slug: "psychics-intuitives", title: "Psychic & intuitive work", description: "Psychics, intuitives, clairvoyants, channelers, and practitioners offering intuitive guidance." },
  { slug: "mediums-mediumship", title: "Mediumship", description: "Mediums, evidential mediumship, spiritualist practice, public demonstrations, and private readings." },
  { slug: "astrologers", title: "Astrology", description: "Natal, predictive, relationship, evolutionary, and other publicly offered astrological practices." },
  { slug: "energy-practitioners", title: "Energy practice", description: "Reiki, energy work, energy healing, and related publicly offered practices and teaching." },
  { slug: "spiritual-teachers-coaches", title: "Spiritual teaching", description: "Meditation, mysticism, nonduality, energy work, healing traditions, and transformational teaching." },
];

const HomePage: React.FC = () => {
  const [query, setQuery] = useState("");
  const [published, setPublished] = useState<ObserverSearchResult[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(
    {},
  );
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([observerApi.directory("", 1, 6), observerApi.categories()])
      .then(([directory, categories]) => {
        setPublished(directory.items || []);
        setCategoryCounts(
          Object.fromEntries(
            categories.map((category) => [category.slug, category.count]),
          ),
        );
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <section className="hero-shell">
        <div className="hero-content">
          <span className="eyebrow">
            <span />
            The public accountability record for the spiritual-practice world
          </span>
          <h1>
            Know the person <em>behind the teaching.</em>
          </h1>
          <p className="hero-copy">
            Research manifestation and law of attraction coaches, law of
            assumption teachers, tarot readers, psychics, mediums, astrologers,
            energy practitioners, and spiritual teachers. Explore what they
            teach, where they publish, how their work has developed, and the
            evidence behind the public record.
          </p>
          <form
            className="hero-search discovery-search"
            onSubmit={(event) => {
              event.preventDefault();
              if (query.trim())
                navigate("/search?q=" + encodeURIComponent(query));
            }}
          >
            <div className="search-field">
              <span className="search-icon">⌕</span>
              <span className="field-copy">
                <small>Search by name</small>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Teacher, coach, reader, psychic, medium, or brand"
                />
              </span>
            </div>
            <button>
              Explore the record <b>→</b>
            </button>
          </form>
          <div className="hero-quick-links">
            <Link to="/write-review">Write a review</Link>
            <Link to="/report?kind=self">Submit your practice</Link>
            <Link to="/trust">How evidence works</Link>
          </div>
        </div>
        <div
          className="hero-proof"
          aria-label="Observer Theory trust principles"
        >
          <div>
            <strong>Their work, in context</strong>
            <span>
              Teachings, official channels, public history, and services are
              kept distinct
            </span>
          </div>
          <div>
            <strong>Evidence on its face</strong>
            <span>
              Observed facts, practitioner statements, and review classes never
              blur together
            </span>
          </div>
          <div>
            <strong>Right of reply, always</strong>
            <span>
              Practitioners can respond without paying, upgrading, or erasing
              the record
            </span>
          </div>
        </div>
      </section>

      <section className="section-shell">
        <div className="section-heading">
          <span className="section-kicker">Practices we document</span>
          <h2>One record across a diverse spiritual landscape.</h2>
          <p>
            Observer Theory identifies each person’s own tradition and language
            without deciding whether a belief, reading, prediction, or
            manifestation method is true.
          </p>
        </div>
        <div className="ot-trust-grid">
          {practices.map((practice) => (
            <Link
              className="ot-practice-card"
              key={practice.slug}
              to={`/practitioners/${practice.slug}`}
            >
              <span>{categoryCounts[practice.slug] || 0} published</span>
              <h2>{practice.title}</h2>
              <p>{practice.description}</p>
              <b>Browse {practice.title.toLowerCase()} records →</b>
            </Link>
          ))}
        </div>
      </section>

      {published.length > 0 && (
        <section className="section-shell ot-home-directory">
          <div className="section-heading">
            <span className="section-kicker">Published practitioner records</span>
            <h2>Browse without already knowing a name.</h2>
            <p>
              These sourced Tier A records passed the automated factual gate. Their
              default labeled order uses sourced establishment and activity facts. Payment, claim status, review sentiment, and transparency observations never affect it.
            </p>
          </div>
          <div className="ot-results ot-directory-results">
            {published.map((row) => (
              <Link
                className="ot-result"
                key={row.entity.publicId}
                to={`/profile/${row.entity.publicId}`}
              >
                <span>{row.entity.entityType}</span>
                <h2>{row.entity.displayName}</h2>
                <p>{row.entity.practiceTags.join(" · ")}</p>
                <small>
                  {row.recordFacts} sourced record facts;{" "}
                  {row.verifiedReviewDepth} verified reviews.
                </small>
              </Link>
            ))}
          </div>
          <Link className="button button-ink" to="/practitioners">
            Browse every published practitioner
          </Link>
        </section>
      )}

      <section className="trust-band">
        <div className="section-shell trust-layout">
          <div className="trust-intro">
            <span className="section-kicker light">
              Research, not a verdict
            </span>
            <h2>
              Spiritual discernment needs more than marketing—or anonymous
              accusation.
            </h2>
            <p>
              Observer Theory brings together sourced public information and
              clearly classed firsthand reviews, then leaves the conclusion with
              you.
            </p>
            <Link className="button button-light" to="/trust">
              Visit the Trust centre
            </Link>
          </div>
          <div className="trust-list">
            <div className="trust-row">
              <span>01</span>
              <div>
                <h3>The Record</h3>
                <p>
                  Identity, teaching focus, public channels, published work,
                  tenure, and—where relevant—programs, readings, courses,
                  prices, and terms.
                </p>
              </div>
            </div>
            <div className="trust-row">
              <span>02</span>
              <div>
                <h3>Provenance</h3>
                <p>
                  Every published record statement carries its evidence class,
                  source, and observation date.
                </p>
              </div>
            </div>
            <div className="trust-row">
              <span>03</span>
              <div>
                <h3>Reviews</h3>
                <p>
                  Verified purchase, unverified client, and opinion on free
                  teachings remain separate. Dimensions are never averaged into
                  an overall rating.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell choice-section">
        <div className="choice-card choice-reader">
          <span className="section-kicker">Looking someone up?</span>
          <h2>Start with a name, not a category ranking.</h2>
          <p>
            Search a teacher, reader, psychic, medium, coach, astrologer,
            creator, or spiritual brand to see whether a sourced record exists.
          </p>
          <div className="choice-actions">
            <Link className="button button-primary" to="/search">
              Search Observer Theory
            </Link>
          </div>
        </div>
        <div className="choice-card choice-pro">
          <span className="section-kicker">Part of this world?</span>
          <h2>Help us document the practice accurately.</h2>
          <p>
            Submit your own public practice or nominate a missing teacher or
            reader. Submission is simple; publication and claiming use separate
            evidence checks.
          </p>
          <div className="choice-actions">
            <Link className="button button-ink" to="/report?kind=self">
              Submit your practice
            </Link>
            <Link to="/report?kind=nomination">Nominate someone</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
